import { config } from '@/config';

// Сессия Matrix для пользователя, уже залогиненного в app. Логинимся НАПРЯМУЮ в homeserver кастомным
// типом `com.graintrade.login.jwt`, передавая существующий Keycloak access-токен app (как старый shell;
// доказано P0-спайком). Полученные Matrix-креды кэшируем per-user; протухание (M_UNKNOWN_TOKEN) лечит
// провайдер повторным логином. Логин делаем через fetch (без matrix-js-sdk), чтобы не тянуть sdk ради входа.

export interface MatrixCreds {
  readonly accessToken: string;
  readonly userId: string;
  readonly deviceId: string;
}

/** Логин не удался (homeserver отверг токен/недоступен) — провайдер покажет состояние ошибки. */
export class MatrixLoginError extends Error {
  constructor(
    message: string,
    readonly status?: number,
    readonly errcode?: string,
  ) {
    super(message);
    this.name = 'MatrixLoginError';
  }
}

/**
 * Отказ логина в homeserver, который означает «чатов пока нет» (пустое состояние), а НЕ сбой связи:
 *  - 403 M_FORBIDDEN — у пользователя ещё нет Matrix-аккаунта (провижнится при создании первой комнаты /
 *    первой сделке), поэтому логин честно отклоняется;
 *  - 429 M_LIMIT_EXCEEDED — homeserver ограничил частоту логинов (rc_login). Реальный пользователь с
 *    аккаунтом держит Matrix-креды в кэше ({@link getCachedCreds}) и логин НЕ повторяет — значит 429 ИМЕННО
 *    на логине возможен лишь у пользователя без кэш-кредов, т.е. без аккаунта, чьи повторные (в итоге-403)
 *    логины упёрлись в лимит. В обоих случаях чатов у него нет → показываем пустое состояние.
 * Сужаем до этих errcode намеренно: иной 403/429 (например от шлюза/WAF, без errcode Synapse), сетевые
 * сбои (MatrixLoginError без status) и 5xx остаются ошибкой связи.
 */
export function isExpectedEmptyChat(error: unknown): boolean {
  if (!(error instanceof MatrixLoginError)) {
    return false;
  }
  return (
    (error.status === 403 && error.errcode === 'M_FORBIDDEN') ||
    (error.status === 429 && error.errcode === 'M_LIMIT_EXCEEDED')
  );
}

const CACHE_SUFFIX = '/chat_credentials';
const cacheKey = (username: string): string => `${username}${CACHE_SUFFIX}`;

export function getCachedCreds(username: string): MatrixCreds | null {
  try {
    const raw = localStorage.getItem(cacheKey(username));
    if (!raw) {
      return null;
    }
    const parsed = JSON.parse(raw) as Partial<MatrixCreds>;
    if (parsed.accessToken && parsed.userId && parsed.deviceId) {
      return { accessToken: parsed.accessToken, userId: parsed.userId, deviceId: parsed.deviceId };
    }
    return null;
  } catch {
    return null;
  }
}

export function cacheCreds(username: string, creds: MatrixCreds): void {
  try {
    localStorage.setItem(cacheKey(username), JSON.stringify(creds));
  } catch {
    // localStorage может быть недоступен (приватный режим/квота) — работаем без кэша, не падаем.
  }
}

export function clearCreds(username: string): void {
  try {
    localStorage.removeItem(cacheKey(username));
  } catch {
    // ignore
  }
}

/** Обменять app-токен на сессию Matrix (`POST /_matrix/client/v3/login`). */
export async function matrixLogin(appToken: string, username: string): Promise<MatrixCreds> {
  let response: Response;
  try {
    response = await fetch(`${config.matrixServerUrl}/_matrix/client/v3/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        type: 'com.graintrade.login.jwt',
        identifier: { type: 'm.id.user', user: username },
        token: appToken,
      }),
    });
  } catch (error) {
    throw new MatrixLoginError(error instanceof Error ? error.message : 'network error');
  }
  const data = (await response.json().catch(() => ({}))) as {
    access_token?: string;
    user_id?: string;
    device_id?: string;
    errcode?: string;
    error?: string;
  };
  if (!response.ok || !data.access_token || !data.user_id || !data.device_id) {
    throw new MatrixLoginError(data.error ?? `login failed (${response.status})`, response.status, data.errcode);
  }
  return { accessToken: data.access_token, userId: data.user_id, deviceId: data.device_id };
}
